/**
 * Region catalog — environment-agnostic loader + indexer.
 * Mirrors @biomax/plugin-pathogen-database/src/catalog.ts.
 */

import type { Region, RawRegionRecord } from './types.js';
import { validateRegion } from './validate.js';

export type CatalogSource = () => RawRegionRecord[];

interface CachedEntry { region: Region; sourcePath: string; }
interface LoadFailure { sourcePath: string; errors: string[]; }

let cacheById: Map<string, CachedEntry> | null = null;
let cacheByIso3: Map<string, CachedEntry> | null = null;
let loadFailures: LoadFailure[] = [];
let warnedAboutFailures = false;

let catalogSource: CatalogSource | null = null;
let dataDirectoryOverride: string | null = null;

export function registerCatalogSource(s: CatalogSource): void {
  catalogSource = s;
  reload();
}

export function __setRegionRecords(records: RawRegionRecord[]): void {
  const frozen = records.slice();
  catalogSource = () => frozen;
  reload();
}

export function __setDataDirectory(path: string | null): void {
  dataDirectoryOverride = path;
  reload();
}

export function getDataDirectoryOverride(): string | null {
  return dataDirectoryOverride;
}

export function reload(): void {
  cacheById = null;
  cacheByIso3 = null;
  loadFailures = [];
  warnedAboutFailures = false;
}

export function getLoadFailures(): readonly LoadFailure[] {
  ensureLoaded();
  return loadFailures;
}

function ensureLoaded(): void {
  if (cacheById !== null) return;
  cacheById = new Map();
  cacheByIso3 = new Map();
  loadFailures = [];
  if (!catalogSource) return;
  const records = catalogSource();
  records.sort((a, b) => a.sourcePath.localeCompare(b.sourcePath));
  for (const raw of records) {
    const result = validateRegion(raw.raw, raw.sourcePath);
    if (!result.valid) {
      loadFailures.push({ sourcePath: raw.sourcePath, errors: result.errors });
      continue;
    }
    const r = result.region;
    if (cacheById.has(r.id)) {
      loadFailures.push({ sourcePath: raw.sourcePath, errors: [`duplicate region id '${r.id}'`] });
      continue;
    }
    cacheById.set(r.id, { region: r, sourcePath: raw.sourcePath });
    if (r.names.iso3) cacheByIso3.set(r.names.iso3.toLowerCase(), { region: r, sourcePath: raw.sourcePath });
  }
  if (loadFailures.length > 0 && !warnedAboutFailures) {
    warnedAboutFailures = true;
    console.warn(`[@biomax/plugin-region-database] ${loadFailures.length} record(s) failed to load`);
  }
}

export function loaderFindRegion(idOrIso3: string): Region | undefined {
  ensureLoaded();
  const direct = cacheById!.get(idOrIso3);
  if (direct) return direct.region;
  const iso = cacheByIso3!.get(idOrIso3.toLowerCase());
  return iso?.region;
}

export function loaderListRegions(): Region[] {
  ensureLoaded();
  return Array.from(cacheById!.values()).map(e => e.region);
}
